/**
 * App Tour catalog — read-side helpers over the generated lesson list.
 * Data comes from docs/tour/lessons.md via scripts/gen-tour-catalog.mjs.
 */
import { resolveProfile, type ProfileUser } from '@/lib/profileIdentity';
import { TOUR_FIRST_DAY, TOUR_FIRST_DAY_WEB, TOUR_LESSONS, TOUR_MODULES } from './tourCatalog.generated';
import type { FactoryAudience, TourLesson, TourModule, TourWhoRule } from './tourTypes';

export type { FactoryAudience, TourLesson, TourModule } from './tourTypes';
export type TourPlatform = 'web' | 'android';
export type TourScope = 'first-day' | 'all';
/**
 * Who is looking.
 * - 'admin' (P18): every lesson, including the admin-only ones (phone setup,
 *   calling/recording and location permissions).
 * - 'manager' (P01): the manager-only lessons, not the admin-only ones.
 * - 'coordinator' (P02): like the manager (Manak, 2026-10-04) — the manager-only
 *   lessons are her manager's screens and read "For managers" for her.
 * - 'hr' (P17): ONLY the "HR and admin" lessons (M18). Every other lesson is
 *   a sales lesson ("All sales", "P01", "P02"…) — HR does not use those screens
 *   (Manak, 2026-10-08: HR gets its own web tour, counted on its own lessons).
 * - 'triage' (P03, Lead Triage Coordinator): ONLY the "Lead triage" lessons — M19 (web desk)
 *   and M20 (Triage app). Manak, 2026-10-08: the triage desk gets its own tour.
 * - 'guard' (P15), 'store' (P14), 'buyer' (P05), 'accounts' (P06): ONLY their own "Factory — …"
 *   lessons (M21, web). Manak, 2026-10-12 (Track 2): each factory role sees only its own lessons.
 * - 'production' (P07, production manager): a factory audience too — ONLY the "Factory — …" lessons
 *   that name "production manager" (M23 BOMs, M27 production orders). 2026-10-27: P07 opens the
 *   App Tour from Help → App Tour, like the other factory roles.
 * - 'team' (everyone else): none of the restricted ones.
 */
export type TourAudience = 'admin' | 'manager' | 'coordinator' | 'hr' | 'triage' | FactoryAudience | 'team';

const FACTORY_PROFILES: Record<string, FactoryAudience> = { P15: 'guard', P14: 'store', P05: 'buyer', P06: 'accounts', P07: 'production' };
/** The factory audiences: web-only lesson lists, no first-day path. */
export function isFactoryAudience(audience: TourAudience): audience is FactoryAudience {
  return audience === 'guard' || audience === 'store' || audience === 'buyer' || audience === 'accounts' || audience === 'production';
}

const BY_ID = new Map(TOUR_LESSONS.map((lesson) => [lesson.id, lesson]));
const FIRST_DAY = new Set([...TOUR_FIRST_DAY, ...TOUR_FIRST_DAY_WEB]);

export function tourAudience(user: ProfileUser | null | undefined): TourAudience {
  const profile = resolveProfile(user);
  if (profile === 'P18') return 'admin';
  if (profile === 'P01') return 'manager';
  if (profile === 'P17') return 'hr';
  if (profile === 'P03') return 'triage';
  if (profile && FACTORY_PROFILES[profile]) return FACTORY_PROFILES[profile];
  return profile === 'P02' ? 'coordinator' : 'team';
}

/** The Tour page's first line: HR, the sales team, or (admin) the whole team. */
export function tourSubtitle(audience: TourAudience): string {
  if (audience === 'hr') return 'Short lessons for HR.';
  if (audience === 'triage') return 'Short lessons for lead triage.';
  if (audience === 'guard') return 'Short lessons for the gate.';
  if (audience === 'store') return 'Short lessons for the store.';
  if (audience === 'buyer') return 'Short lessons for purchase.';
  if (audience === 'accounts') return 'Short lessons for accounts.';
  if (audience === 'production') return 'Short lessons for production.';
  if (audience === 'admin') return 'Short lessons for your team.';
  return 'Short lessons for the sales team.';
}

const MANAGER_ONLY = /^manager only/i;

/** A coordinator reads "Manager only (…)" as "For managers (…)": her manager's screens, not hers. */
function forAudience(text: string, audience: TourAudience): string {
  return audience === 'coordinator' ? text.replace(MANAGER_ONLY, 'For managers') : text;
}

export function moduleTitleFor(module: TourModule | undefined, audience: TourAudience): string {
  return module ? forAudience(module.title, audience) : '';
}

export function lessonWhoFor(lesson: TourLesson, audience: TourAudience): string {
  return forAudience(lesson.who, audience);
}

export function findLesson(id: string | undefined): TourLesson | undefined {
  return id ? BY_ID.get(id) : undefined;
}

export function findModule(id: string | undefined): TourModule | undefined {
  return TOUR_MODULES.find((module) => module.id === id);
}

/**
 * M22 who-rules. The HR, triage and factory tours are short lists of their own (their counts stay as they were),
 * so "Everyone" and "Page note writers" never add a lesson to them. `notesAllowed` is `usePageNotesWriter()`.
 */
export function whoRuleAllows(rule: TourWhoRule, audience: TourAudience, notesAllowed: boolean): boolean {
  if (rule === 'adminOwner') return audience === 'admin';
  if (audience === 'hr' || audience === 'triage' || isFactoryAudience(audience)) return false;
  return rule === 'everyone' || notesAllowed;
}

/** Role filter: admin-only → admin; HR-only → HR and admin; triage-only → P03 and admin; factory → its named roles and admin; HR, triage and factory roles see nothing else; manager-only → manager, coordinator and admin; the rest → everyone. */
export function canSeeLesson(lesson: TourLesson, audience: TourAudience, notesAllowed = false): boolean {
  if (lesson.whoRule) return whoRuleAllows(lesson.whoRule, audience, notesAllowed);
  if (lesson.adminOnly) return audience === 'admin';
  if (lesson.hrOnly) return audience === 'hr' || audience === 'admin';
  if (lesson.triageOnly) return audience === 'triage' || audience === 'admin';
  if (lesson.factoryRoles?.length) return audience === 'admin' || (isFactoryAudience(audience) && lesson.factoryRoles.includes(audience));
  if (audience === 'hr' || audience === 'triage' || isFactoryAudience(audience)) return false;
  if (lesson.managerOnly) return audience === 'manager' || audience === 'coordinator' || audience === 'admin';
  return true;
}

/** Web lists Web + Both lessons; the Android switch lists Android + Both. */
export function onPlatform(lesson: TourLesson, platform: TourPlatform): boolean {
  return lesson.platform === 'Both' || lesson.platform === (platform === 'web' ? 'Web' : 'Android');
}

/**
 * "Allowed": the lesson list says the lesson may run as a walk. Android lessons are
 * watch-only on the web: the guided walk needs the real web page.
 * Whether a walk is actually wired is `hasWalk` in tourWalks.ts.
 */
export function canShowMe(lesson: TourLesson, platform: TourPlatform): boolean {
  return platform === 'web' && lesson.showMe.allowed;
}

export function lessonMinutes(lesson: TourLesson, platform: TourPlatform): number {
  return lesson.minutes[platform] ?? lesson.minutes.web ?? lesson.minutes.android ?? 0;
}

export function matchesSearch(lesson: TourLesson, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const module = findModule(lesson.moduleId);
  return `${lesson.id} ${lesson.title} ${module?.title ?? ''}`.toLowerCase().includes(q);
}

export interface TourFilter {
  audience: TourAudience;
  /** May this person write page notes (`usePageNotesWriter`)? Only the "Page note writers" lessons read it. */
  notesAllowed?: boolean;
  platform: TourPlatform;
  scope: TourScope;
}

/** Every lesson this person can open on this platform, in catalog order. */
export function visibleLessons({ audience, platform, scope, notesAllowed = false }: TourFilter): TourLesson[] {
  return TOUR_LESSONS.filter((lesson) =>
    canSeeLesson(lesson, audience, notesAllowed) && onPlatform(lesson, platform) && (scope === 'all' || FIRST_DAY.has(lesson.id)));
}

export function visibleModules(lessons: TourLesson[]): TourModule[] {
  const ids = new Set(lessons.map((lesson) => lesson.moduleId));
  return TOUR_MODULES.filter((module) => ids.has(module.id));
}
